/*
 * Newsletter popup — lightweight, dependency-free Web Component.
 * Production contract for an optional endpoint:
 * POST { email, market, locale, consent, source }
 * 200 { success: true }
 *
 * The unique Shopify discount code is generated by the Omnisend Discount
 * block and delivered in the localized welcome email.
 */
(function () {
  "use strict";

  const script = document.currentScript;
  const scriptOptions = script
    ? {
        endpoint: script.dataset.apiEndpoint || "",
        market: script.dataset.market || "",
        privacyFr: script.dataset.privacyFr || "",
        privacyIt: script.dataset.privacyIt || "",
        privacyUs: script.dataset.privacyUs || "",
        logoUrl: script.dataset.logoUrl || "",
        delay: Number(script.dataset.delay || 7000),
        dismissDays: Number(script.dataset.dismissDays || 7),
        demo: script.dataset.demo === "true",
      }
    : {};

  const COPY = {
    FR: {
      lang: "fr",
      eyebrow: "Céleste Mogador",
      title: "Un cadeau pour vous.",
      body: "Entrez dans l’univers Céleste Mogador. Inscrivez-vous à la newsletter et profitez de 10 % de remise sur votre première commande.",
      placeholder: "Votre adresse e-mail",
      emailLabel: "Adresse e-mail",
      cta: "Recevoir mes −10 %",
      submitting: "Inscription en cours…",
      consentPrefix: "J’accepte de recevoir les actualités et offres de Céleste Mogador par e-mail. Je peux retirer mon consentement à tout moment. Consulter la ",
      privacy: "Politique de confidentialité",
      consentSuffix: ".",
      close: "Fermer",
      invalidEmail: "Saisissez une adresse e-mail valide.",
      consentRequired: "Veuillez accepter de recevoir nos communications pour vous inscrire.",
      genericError: "L’inscription n’a pas pu être finalisée. Veuillez réessayer.",
      successEyebrow: "Bienvenue",
      successTitle: "Consultez votre boîte mail.",
      successBody: "Votre inscription est confirmée. Votre code personnel de 10 % vient de vous être envoyé par e-mail.",
      terms: "Code individuel, valable 14 jours sur votre première commande. Les éventuelles exclusions figurent dans l’e-mail.",
    },
    IT: {
      lang: "it",
      eyebrow: "Céleste Mogador",
      title: "Un regalo per te.",
      body: "Entra nell’universo Céleste Mogador. Iscriviti alla newsletter e ricevi il 10% di sconto sul tuo primo ordine.",
      placeholder: "Il tuo indirizzo email",
      emailLabel: "Indirizzo email",
      cta: "Ricevi il mio −10%",
      submitting: "Iscrizione in corso…",
      consentPrefix: "Acconsento a ricevere comunicazioni commerciali via email. Posso revocare il consenso in qualsiasi momento. Leggi l’",
      privacy: "Informativa sulla privacy",
      consentSuffix: ".",
      close: "Chiudi",
      invalidEmail: "Inserisci un indirizzo email valido.",
      consentRequired: "Per iscriverti, devi fornire il consenso.",
      genericError: "Non è stato possibile completare l’iscrizione. Riprova.",
      successEyebrow: "Benvenuto",
      successTitle: "Controlla la tua email.",
      successBody: "La tua iscrizione è confermata. Ti abbiamo appena inviato via email il tuo codice personale del 10%.",
      terms: "Codice individuale valido per 14 giorni sul primo ordine. Eventuali esclusioni sono indicate nell’email.",
    },
    US: {
      lang: "en-US",
      eyebrow: "Céleste Mogador",
      title: "A gift for you.",
      body: "Step into the world of Céleste Mogador. Join our newsletter and enjoy 10% off your first order.",
      placeholder: "Your email address",
      emailLabel: "Email address",
      cta: "Get my 10% off",
      submitting: "Signing you up…",
      consentPrefix: "By signing up, you agree to receive marketing emails. You can unsubscribe at any time. View our ",
      privacy: "Privacy Policy",
      consentSuffix: ".",
      close: "Close",
      invalidEmail: "Enter a valid email address.",
      consentRequired: "Please provide your consent to subscribe.",
      genericError: "We couldn’t complete your sign-up. Please try again.",
      successEyebrow: "Welcome",
      successTitle: "Check your inbox.",
      successBody: "You’re subscribed. We’ve just emailed your personal 10% off code.",
      terms: "Individual code valid for 14 days on your first order. Any exclusions appear in the email.",
    },
  };

  const CSS = `
    :host { all: initial; color: #171714; font-family: Inter, Arial, sans-serif; }
    *, *::before, *::after { box-sizing: border-box; }
    .backdrop { position: fixed; inset: 0; z-index: 2147483000; display: grid; place-items: center; padding: 20px; background: rgba(17, 17, 14, .54); opacity: 0; transition: opacity 180ms ease; }
    .backdrop[data-open="true"] { opacity: 1; }
    .dialog { position: relative; width: min(100%, 500px); overflow: hidden; border: 1px solid rgba(23, 23, 20, .18); background: #faf6ee; box-shadow: 0 24px 80px rgba(0,0,0,.24); transform: translateY(12px) scale(.985); transition: transform 220ms cubic-bezier(.2,.8,.2,1); }
    .backdrop[data-open="true"] .dialog { transform: translateY(0) scale(1); }
    .accent { height: 10px; background: #96a9e6; }
    .content { padding: 46px 46px 40px; }
    .logo { display: block; width: 124px; height: auto; margin: 0 0 20px; }
    .eyebrow { margin: 0 0 18px; color: #171714; font-size: 11px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; }
    h2 { max-width: 400px; margin: 0; font-family: Marcellus, Georgia, serif; font-size: clamp(34px, 7vw, 50px); font-weight: 400; letter-spacing: -.02em; line-height: 1.02; text-transform: uppercase; }
    .body { max-width: 390px; margin: 18px 0 24px; color: #4f4c44; font-size: 16px; line-height: 1.55; }
    form { display: grid; gap: 12px; }
    .email { width: 100%; min-height: 52px; padding: 0 16px; border: 1px solid #8e8b84; border-radius: 0; background: #fffdf8; color: #171714; font: inherit; outline: none; }
    .email:focus { border-color: #171714; box-shadow: 0 0 0 3px rgba(23,23,20,.12); }
    .email[aria-invalid="true"] { border-color: #a72716; }
    .submit { min-height: 52px; border: 0; border-radius: 0; background: #171714; color: #fff; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; transition: transform 120ms ease, background 120ms ease; }
    .submit:hover { background: #34342f; }
    .submit:active { transform: translateY(1px); }
    .submit:focus-visible, .close:focus-visible, a:focus-visible { outline: 3px solid #96a9e6; outline-offset: 3px; }
    .submit:disabled { cursor: wait; opacity: .65; }
    .consent { display: grid; grid-template-columns: 18px 1fr; gap: 9px; align-items: start; color: #625e55; font-size: 12px; line-height: 1.45; }
    .consent input { width: 16px; height: 16px; margin: 1px 0 0; accent-color: #171714; }
    .disclosure { margin: 2px 0 0; color: #625e55; font-size: 12px; line-height: 1.45; }
    a { color: inherit; text-underline-offset: 2px; }
    .error { min-height: 18px; margin: 0; color: #a72716; font-size: 12px; line-height: 1.4; }
    .close { position: absolute; top: 22px; right: 22px; display: grid; width: 38px; height: 38px; place-items: center; border: 1px solid rgba(23,23,20,.18); border-radius: 0; background: #faf6ee; color: #171714; cursor: pointer; font: 24px/1 sans-serif; }
    .close:hover { background: #fff; }
    .success { text-align: center; }
    .success .eyebrow { margin-top: 8px; }
    .success h2 { margin-inline: auto; }
    .success .body { margin-inline: auto; }
    .terms { margin: 14px 0 0; color: #716c62; font-size: 11px; line-height: 1.45; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
    @media (max-width: 540px) {
      .backdrop { align-items: end; padding: 12px; }
      .dialog { width: min(100%, 340px); max-height: calc(100svh - 24px); overflow-y: auto; }
      .accent { height: 7px; }
      .content { padding: 28px 20px 22px; }
      .logo { width: 82px; margin-bottom: 12px; }
      .eyebrow { margin-bottom: 12px; font-size: 10px; }
      h2 { padding-right: 34px; font-size: 29px; line-height: 1.05; }
      .body { margin: 12px 0 16px; font-size: 14px; line-height: 1.45; }
      form { gap: 9px; }
      .email, .submit { min-height: 46px; }
      .consent, .disclosure { font-size: 11px; line-height: 1.35; }
      .close { top: 14px; right: 14px; width: 34px; height: 34px; font-size: 22px; }
    }
    @media (prefers-reduced-motion: reduce) { .backdrop, .dialog { transition: none; } }
  `;

  const defaults = { endpoint: "", market: "", privacyFr: "", privacyIt: "", privacyUs: "",
    logoUrl: "", delay: 7000, dismissDays: 7, demo: false };
  let config = { ...defaults, ...scriptOptions };
  let instance;
  let scheduled = false;
  let shownThisPage = false;
  let delayTimer;

  function detectMarket(explicitMarket) {
    const market = String(explicitMarket || "").toUpperCase();
    if (market === "FR" || market === "IT" || market === "US") return market;
    const pageLang = (document.documentElement.lang || "").toLowerCase();
    if (pageLang.startsWith("fr")) return "FR";
    if (pageLang.startsWith("it")) return "IT";
    if (pageLang.startsWith("en")) return "US";
    const shopifyCountry = window.Shopify && String(window.Shopify.country || "").toUpperCase();
    if (shopifyCountry === "FR" || shopifyCountry === "IT" || shopifyCountry === "US") return shopifyCountry;
    const browserLang = (navigator.language || "en-US").toLowerCase();
    if (browserLang.startsWith("fr")) return "FR";
    return browserLang.startsWith("it") ? "IT" : "US";
  }
  function storageKey(name) { return `newsletter_popup_v1_${name}`; }
  function readStorage(name) { try { return localStorage.getItem(storageKey(name)); } catch (_) { return null; } }
  function writeStorage(name, value) { try { localStorage.setItem(storageKey(name), String(value)); } catch (_) {} }
  function shouldSuppress() {
    if (readStorage("subscribed") === "true") return true;
    const dismissedAt = Number(readStorage("dismissed_at") || 0);
    return dismissedAt > 0 && Date.now() - dismissedAt < config.dismissDays * 86400000;
  }
  function track(action, details = {}) {
    const payload = { event: "newsletter_popup", newsletter_action: action, newsletter_market: detectMarket(config.market), newsletter_variant: "welcome_10_v1", ...details };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent("newsletter:analytics", { detail: payload }));
  }

  class NewsletterPopupElement extends HTMLElement {
    constructor() { super(); this.market = detectMarket(config.market); this.copy = COPY[this.market]; this.previouslyFocused = null; this.attachShadow({ mode: "open" }); }
    connectedCallback() {
      this.renderForm(); this.bindEvents(); this.previouslyFocused = document.activeElement;
      requestAnimationFrame(() => { this.shadowRoot.querySelector(".backdrop").dataset.open = "true"; this.shadowRoot.querySelector(".email").focus({ preventScroll: true }); });
      track("impression");
    }
    privacyUrl() {
      const configured = this.market === "FR" ? config.privacyFr : this.market === "IT" ? config.privacyIt : config.privacyUs;
      if (configured) return configured;
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
      return `${root}policies/privacy-policy`;
    }
    renderForm() {
      const t = this.copy;
      const requiresExplicitConsent = this.market === "IT" || this.market === "FR";
      const brand = config.logoUrl
        ? `<img class="logo" src="${config.logoUrl}" alt="Céleste Mogador" width="124" height="123" decoding="async" fetchpriority="low">`
        : `<p class="eyebrow">${t.eyebrow}</p>`;
      this.shadowRoot.innerHTML = `
        <style>${CSS}</style>
        <div class="backdrop" data-open="false">
          <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="newsletter-title" lang="${t.lang}">
            <div class="accent" aria-hidden="true"></div>
            <button class="close" type="button" aria-label="${t.close}">×</button>
            <div class="content">
              ${brand}<h2 id="newsletter-title">${t.title}</h2><p class="body">${t.body}</p>
              <form novalidate>
                <label class="sr-only" for="newsletter-email">${t.emailLabel}</label>
                <input class="email" id="newsletter-email" name="email" type="email" inputmode="email" autocomplete="email" placeholder="${t.placeholder}" required>
                ${requiresExplicitConsent ? `<label class="consent"><input name="consent" type="checkbox" required><span>${t.consentPrefix}<a href="${this.privacyUrl()}" target="_blank" rel="noopener">${t.privacy}</a>${t.consentSuffix}</span></label>` : `<p class="disclosure">${t.consentPrefix}<a href="${this.privacyUrl()}" target="_blank" rel="noopener">${t.privacy}</a>${t.consentSuffix}</p>`}
                <p class="error" role="alert" aria-live="polite"></p><button class="submit" type="submit">${t.cta}</button>
              </form>
            </div>
          </section>
        </div>`;
    }
    bindEvents() {
      const root = this.shadowRoot;
      root.querySelector(".close").addEventListener("click", () => this.close("button"));
      root.querySelector(".backdrop").addEventListener("click", (event) => { if (event.target.classList.contains("backdrop")) this.close("backdrop"); });
      root.querySelector("form").addEventListener("submit", (event) => this.submit(event));
      this.addEventListener("keydown", (event) => this.handleKeydown(event));
    }
    handleKeydown(event) {
      if (event.key === "Escape") return this.close("escape");
      if (event.key !== "Tab") return;
      const focusable = [...this.shadowRoot.querySelectorAll("button, input, a[href]")].filter((el) => !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && this.shadowRoot.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && this.shadowRoot.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    close(method) {
      writeStorage("dismissed_at", Date.now()); track("close", { newsletter_close_method: method });
      const backdrop = this.shadowRoot.querySelector(".backdrop"); backdrop.dataset.open = "false";
      window.setTimeout(() => { this.remove(); if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus(); }, 220);
    }
    async submit(event) {
      event.preventDefault();
      const t = this.copy, form = event.currentTarget, email = form.elements.email, consent = form.elements.consent;
      const error = this.shadowRoot.querySelector(".error"), button = this.shadowRoot.querySelector(".submit");
      error.textContent = ""; email.setAttribute("aria-invalid", "false");
      if (!email.validity.valid) { email.setAttribute("aria-invalid", "true"); error.textContent = t.invalidEmail; email.focus(); track("validation_error", { newsletter_error: "invalid_email" }); return; }
      if ((this.market === "IT" || this.market === "FR") && consent && !consent.checked) { error.textContent = t.consentRequired; consent.focus(); track("validation_error", { newsletter_error: "consent_required" }); return; }
      button.disabled = true; button.textContent = t.submitting; track("submit");
      try { await this.subscribe(email.value.trim(), Boolean(consent ? consent.checked : true)); writeStorage("subscribed", "true"); track("success", { newsletter_delivery: "unique_code_by_email" }); this.renderSuccess(); }
      catch (requestError) { error.textContent = requestError.message || t.genericError; button.disabled = false; button.textContent = t.cta; track("error", { newsletter_error: "request_failed" }); }
    }
    async subscribe(email, consent) {
      if (config.demo) { await new Promise((resolve) => window.setTimeout(resolve, 500)); return true; }
      if (!config.endpoint && window.Shopify) { await this.subscribeViaShopify(email); return true; }
      if (!config.endpoint) throw new Error(this.copy.genericError);
      const controller = new AbortController(), timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(config.endpoint, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ email, market: this.market, locale: this.copy.lang, consent, source: "homepage_popup" }), signal: controller.signal });
        if (!response.ok) throw new Error(this.copy.genericError);
        const data = await response.json(); if (!data || data.success !== true) throw new Error(this.copy.genericError); return true;
      } finally { window.clearTimeout(timeout); }
    }
    subscribeViaShopify(email) {
      const shopifyForm = document.getElementById("CelesteNewsletterPopupBridge");
      if (!shopifyForm) throw new Error(this.copy.genericError);
      let emailInput = shopifyForm.querySelector('[name="contact[email]"]');
      let tagsInput = shopifyForm.querySelector('[name="contact[tags]"]');
      if (!emailInput) { emailInput = document.createElement("input"); emailInput.type = "email"; emailInput.name = "contact[email]"; shopifyForm.appendChild(emailInput); }
      if (!tagsInput) { tagsInput = document.createElement("input"); tagsInput.type = "hidden"; tagsInput.name = "contact[tags]"; shopifyForm.appendChild(tagsInput); }
      emailInput.value = email;
      tagsInput.value = `newsletter,newsletter-popup,form:homepage-popup,offer:welcome10-unique,language:${this.copy.lang.toLowerCase()},market:${this.market.toLowerCase()}`;
      writeStorage("subscribed", "true");
      track("shopify_subscriber_submitted", { newsletter_sync_target: "omnisend" });
      shopifyForm.requestSubmit();
      return new Promise(() => {});
    }
    renderSuccess() {
      const t = this.copy;
      this.shadowRoot.querySelector(".content").innerHTML = `<div class="success" role="status" aria-live="polite"><p class="eyebrow">${t.successEyebrow}</p><h2 id="newsletter-title">${t.successTitle}</h2><p class="body">${t.successBody}</p><p class="terms">${t.terms}</p></div>`;
      this.shadowRoot.querySelector(".success").setAttribute("tabindex", "-1"); this.shadowRoot.querySelector(".success").focus();
    }
  }
  function open(options = {}) { config = { ...config, ...options }; if (instance && instance.isConnected) return; if (!customElements.get("newsletter-popup")) customElements.define("newsletter-popup", NewsletterPopupElement); instance = document.createElement("newsletter-popup"); document.body.appendChild(instance); shownThisPage = true; }
  function schedule(options = {}) {
    config = { ...config, ...options }; if (scheduled || shownThisPage || shouldSuppress()) return; scheduled = true;
    const show = () => { if (shownThisPage || shouldSuppress() || document.visibilityState !== "visible") return; window.clearTimeout(delayTimer); document.removeEventListener("mouseout", onExitIntent); open(); };
    const onExitIntent = (event) => { if (window.matchMedia("(pointer: fine)").matches && event.clientY <= 0 && !event.relatedTarget) show(); };
    delayTimer = window.setTimeout(show, Math.max(3000, config.delay)); document.addEventListener("mouseout", onExitIntent);
  }
  function resetDemoState() { try { localStorage.removeItem(storageKey("subscribed")); localStorage.removeItem(storageKey("dismissed_at")); } catch (_) {} shownThisPage = false; scheduled = false; }
  window.NewsletterPopup = { open, schedule, resetDemoState, track };
  function boot() {
    const enqueue = () => { if ("requestIdleCallback" in window) requestIdleCallback(() => schedule(), { timeout: 2500 }); else window.setTimeout(() => schedule(), 1); };
    if (document.readyState === "complete") enqueue(); else window.addEventListener("load", enqueue, { once: true });
  }
  boot();
})();
